/**
 * B2 — Resultado del ejercicio y borradores orientativos de los modelos 303 (IVA) y 130 (IRPF)
 * del emisor con libros del autónomo. NO son los modelos oficiales: la gestoría los revisa y presenta.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { AlertCircle, Info, Loader2, ShieldCheck } from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import type { BooksSummaryResponse } from '@toga/shared'
import { Note, td, tdR, th, thR } from './common'

interface Row {
  label: ReactNode
  values: number[]
  /** Valor de la columna del año; `null` la deja vacía. */
  year: number | null
  bold?: boolean
  hint?: (v: number) => string | null
  indent?: boolean
}

/** Evita «-0,00 €» al negar un cero. */
const nz = (n: number): number => (n === 0 ? 0 : n)

function QuarterTable({
  title,
  intro,
  rows,
  year,
}: {
  title: string
  intro: string
  rows: Row[]
  year: number
}) {
  return (
    <section>
      <div className="px-5 pt-5 pb-2">
        <h3 className="text-sm font-semibold t-primary">{title}</h3>
        <p className="text-xs t-muted mt-0.5 max-w-3xl">{intro}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-base">
              <th className={th} />
              {[1, 2, 3, 4].map((q) => (
                <th key={q} className={thR}>
                  {q}.º trim.
                </th>
              ))}
              <th className={thR}>{year}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={i}
                className={['border-b border-base/50', r.bold ? 'font-semibold' : ''].join(' ')}
              >
                <td className={`${td} ${r.indent ? 'pl-7 t-muted text-xs' : ''}`}>{r.label}</td>
                {r.values.map((v, qi) => (
                  <td key={qi} className={tdR}>
                    {formatCents(nz(v))}
                    {r.hint?.(v) && (
                      <span className="block text-[10px] t-faint font-normal">{r.hint(v)}</span>
                    )}
                  </td>
                ))}
                <td className={tdR}>
                  {r.year === null ? '' : formatCents(nz(r.year))}
                  {r.year !== null && r.hint?.(r.year) && (
                    <span className="block text-[10px] t-faint font-normal">{r.hint(r.year)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

const sum = (xs: number[]) => xs.reduce((a, x) => a + x, 0)

export default function TaxTab({ issuerId, year }: { issuerId: string; year: number }) {
  const [data, setData] = useState<BooksSummaryResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setData(null)
    setError(null)
    api
      .get<BooksSummaryResponse>(`/books/summary?year=${year}&issuerId=${issuerId}`)
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Error al cargar'))
    return () => {
      alive = false
    }
  }, [issuerId, year])

  if (error) {
    return (
      <div className="m-4 text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
        <AlertCircle size={13} /> {error}
      </div>
    )
  }
  if (!data) {
    return (
      <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    )
  }

  const qs = data.quarters
  const v = data.vat303
  const t = data.irpf130
  const result = (n: number) => (n > 0 ? 'a ingresar' : n < 0 ? 'a compensar o devolver' : null)
  const pay = (n: number) => (n > 0 ? 'a ingresar' : 'sin pago')
  const share = data.retentionShare

  return (
    <div className="divide-y divide-base">
      <div className="px-5 py-3 flex items-start gap-2 text-xs text-amber-200 bg-amber-500/10">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>
          Borradores orientativos de <strong>{data.issuer.name}</strong> ({data.issuer.nif}). No son
          los modelos oficiales ni se presentan desde Toga: tu gestoría los revisa y presenta.
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-base,transparent)]">
        {[
          ['Ingresos (base)', data.totals.incomeBaseCents],
          ['Gastos y amortización', data.totals.expensesCents + data.totals.amortizationCents],
          ['Rendimiento neto', data.totals.netResultCents],
          ['130 a ingresar en el año', sum(t.map((q) => q.paymentCents))],
        ].map(([label, value]) => (
          <div key={label as string} className="px-5 py-4">
            <p className="text-[11px] uppercase tracking-wider t-faint">{label}</p>
            <p className="text-xl font-serif font-semibold t-primary mt-1">
              {formatCents(value as number)}
            </p>
          </div>
        ))}
      </div>

      <QuarterTable
        year={year}
        title="Resultado del ejercicio"
        intro="Ingresos de las facturas emitidas (sin suplidos) menos los gastos deducibles y la amortización de los bienes de inversión."
        rows={[
          {
            label: 'Ingresos (base imponible)',
            values: qs.map((q) => q.incomeBaseCents),
            year: data.totals.incomeBaseCents,
          },
          {
            label: 'Gastos deducibles',
            values: qs.map((q) => -q.expensesCents),
            year: -data.totals.expensesCents,
          },
          {
            label: 'Amortización',
            values: qs.map((q) => -q.amortizationCents),
            year: -data.totals.amortizationCents,
          },
          {
            label: 'Rendimiento neto',
            values: qs.map((q) => q.netResultCents),
            year: data.totals.netResultCents,
            bold: true,
          },
        ]}
      />

      <QuarterTable
        year={year}
        title="Borrador del modelo 303 · IVA"
        intro="IVA repercutido en tus facturas menos el IVA soportado deducible de las facturas recibidas, por trimestre."
        rows={[
          {
            label: 'Base imponible repercutida',
            values: v.map((q) => q.accruedBaseCents),
            year: sum(v.map((q) => q.accruedBaseCents)),
          },
          {
            label: 'de ello, a tipo 0 % (exentas o no sujetas)',
            values: v.map((q) => q.exemptBaseCents),
            year: sum(v.map((q) => q.exemptBaseCents)),
            indent: true,
          },
          {
            label: 'IVA repercutido (devengado)',
            values: v.map((q) => q.accruedVatCents),
            year: data.totals.accruedVatCents,
          },
          {
            label: 'IVA soportado deducible · gastos corrientes',
            values: v.map((q) => -q.deductibleCurrentVatCents),
            year: -sum(v.map((q) => q.deductibleCurrentVatCents)),
          },
          {
            label: 'IVA soportado deducible · bienes de inversión',
            values: v.map((q) => -q.deductibleInvestmentVatCents),
            year: -sum(v.map((q) => q.deductibleInvestmentVatCents)),
          },
          {
            label: 'Resultado',
            values: v.map((q) => q.resultCents),
            year: sum(v.map((q) => q.resultCents)),
            bold: true,
            hint: result,
          },
        ]}
      />

      <QuarterTable
        year={year}
        title="Borrador del modelo 130 · IRPF (pago fraccionado)"
        intro="Se calcula sobre lo acumulado del año: el 20 % del rendimiento neto, menos las retenciones que te han practicado y lo pagado en trimestres anteriores."
        rows={[
          {
            label: 'Ingresos acumulados',
            values: t.map((q) => q.ytdIncomeCents),
            year: t[3]?.ytdIncomeCents ?? 0,
          },
          {
            label: 'Gastos acumulados (con amortización)',
            values: t.map((q) => -q.ytdExpensesCents),
            year: -(t[3]?.ytdExpensesCents ?? 0),
          },
          {
            label: 'Rendimiento neto acumulado',
            values: t.map((q) => q.ytdNetCents),
            year: t[3]?.ytdNetCents ?? 0,
          },
          {
            label: '20 % del rendimiento neto',
            values: t.map((q) => q.ratePaymentCents),
            year: t[3]?.ratePaymentCents ?? 0,
          },
          {
            label: 'Retenciones soportadas acumuladas',
            values: t.map((q) => -q.ytdRetentionsCents),
            year: -(t[3]?.ytdRetentionsCents ?? 0),
          },
          {
            label: 'Pagos de trimestres anteriores',
            values: t.map((q) => -q.previousPaymentsCents),
            year: -(t[3]?.previousPaymentsCents ?? 0),
          },
          {
            label: 'A ingresar en el trimestre',
            values: t.map((q) => q.paymentCents),
            year: sum(t.map((q) => q.paymentCents)),
            bold: true,
            hint: pay,
          },
        ]}
      />

      <div className="px-5 py-4">
        <h3 className="text-sm font-semibold t-primary flex items-center gap-2">
          <ShieldCheck size={15} className="text-toga-300" /> Prueba del 70 % de retenciones
        </h3>
        {share.percent === null ? (
          <p className="text-xs t-muted mt-1 max-w-3xl">
            En {share.previousYear} no hay facturas emitidas con este emisor, así que no se puede
            comprobar si estás exento de presentar el 130. Pregúntalo a tu gestoría.
          </p>
        ) : (
          <p className="text-xs t-muted mt-1 max-w-3xl">
            En {share.previousYear}, el{' '}
            <strong className="t-primary">{share.percent.toLocaleString('es-ES')} %</strong> de tus
            ingresos ({formatCents(share.retainedBaseCents)} de {formatCents(share.totalBaseCents)})
            llevó retención.{' '}
            {share.likelyExempt
              ? 'Con un 70 % o más, normalmente no hay obligación de presentar el modelo 130: confírmalo con tu gestoría antes de dejarlo de presentar.'
              : 'Por debajo del 70 %, hay que presentar el modelo 130 cada trimestre.'}
          </p>
        )}
      </div>

      <Note>
        No incluyen prorrata, regímenes especiales de IVA, operaciones con la UE ni inversión del
        sujeto pasivo, ni las deducciones específicas del 130 ni las compensaciones de otros
        ejercicios. Los trimestres anteriores del 130 se calculan con este mismo criterio; si se
        presentaron otros importes, hay que ajustarlo. La retención se atribuye al trimestre de la
        fecha de la factura.
      </Note>
    </div>
  )
}
